<script setup lang="ts">
  import type { AgencyStructureNode } from '@gat-ship/types'
  import type {
    ComboboxFieldItem,
    ComboboxFieldProps
  } from '@gat-ship/vue-components'
  import { computed, inject, onMounted, onUnmounted, ref, unref } from 'vue'
  import { testIds } from '@gat-ship/types'
  import { useAgencyStructure } from '../../composables/useAgencyStructure'
  import { agencyStructureKey } from '@gat-ship/types/injection-keys'
  import { ComboboxField } from '@gat-ship/vue-components'

  export interface SelectAgencyStructureProps extends Omit<
    ComboboxFieldProps,
    'items' | 'modelValue'
  > {
    items?: AgencyStructureNode[]
    showInactiveAgencies?: boolean
    preSelectedAgency?: AgencyStructureNode
    /** Whether to fetch the agencies on mount. @default true */
    fetchOnMount?: boolean
    options?: {
      includeInactive?: boolean
      forceFetch?: boolean
    }
  }

  const props = withDefaults(defineProps<SelectAgencyStructureProps>(), {
    options: () => ({
      includeInactive: false,
      forceFetch: false
    })
  })

  const emit = defineEmits<{
    (e: 'agency-structure-fetched', payload: AgencyStructureNode[]): void
  }>()

  const agencyStructure = inject(agencyStructureKey, useAgencyStructure())

  function subscribeToAgencyEvents() {
    agencyStructure.subscribeToEvents()
  }

  function unsubscribeFromAgencyEvents() {
    agencyStructure.unsubscribeFromEvents()
  }

  async function fetchAgencyStructure() {
    const result = await agencyStructure.fetchAgencyStructure(props.options)
    if (result) {
      emit('agency-structure-fetched', result)
    }
    return result
  }

  onMounted(async () => {
    subscribeToAgencyEvents()
    if (props.fetchOnMount) {
      await fetchAgencyStructure()
    }
  })

  onUnmounted(() => {
    unsubscribeFromAgencyEvents()
  })

  const selectedAgencyId = defineModel<AgencyStructureNode['id'] | undefined>({
    required: true
  })

  const isOpen = defineModel<boolean>('open', { default: false })

  const preSelectedAgencyRef = ref<AgencyStructureNode | undefined>(undefined)

  interface NodeMetaData {
    name: string
    shortName: string
    level: number
    nodeType: 'agency' | 'agency-group'
  }

  const _items = computed(() => {
    const allNodes = unref(agencyStructure.agencyStructure) ?? []
    const showInactive =
      props.showInactiveAgencies ?? props.options.includeInactive ?? false
    // Filter inactive agencies by default, but always keep the
    // currently-selected one — a URL pointing at a now-deactivated
    // agency's settings page still needs to render its entry so the
    // user can see where they are and navigate away.
    const visibleNodes = showInactive
      ? allNodes
      : allNodes.filter((i) => !i.isInactive || i.id === selectedAgencyId.value)
    const result: ComboboxFieldItem<NodeMetaData>[] = visibleNodes.map((i) => ({
      title: i.name,
      value: i.id,
      searchValue: `${i.name} ${i.shortName}`,
      metaData: {
        name: i.name,
        shortName: i.shortName,
        level: i.level,
        nodeType: i.$type
      }
    }))
    const selectedAgencyRef =
      preSelectedAgencyRef.value || props.preSelectedAgency
    if (
      selectedAgencyRef &&
      !result.find((i) => i.value === selectedAgencyRef.id)
    ) {
      result.push({
        title: selectedAgencyRef.name,
        value: selectedAgencyRef.id,
        searchValue: `${selectedAgencyRef.name} ${selectedAgencyRef.shortName}`,
        metaData: {
          name: selectedAgencyRef.name,
          shortName: selectedAgencyRef.shortName,
          level: selectedAgencyRef.level,
          nodeType: selectedAgencyRef.$type
        }
      })
    }
    return result
  })

  defineExpose({
    fetchAgencyStructure,
    setPreSelectedAgency: (agency: AgencyStructureNode) => {
      preSelectedAgencyRef.value = agency
    }
  })
</script>
<template>
  <ComboboxField
    v-bind="props"
    v-model="selectedAgencyId"
    v-model:open="isOpen"
    :data-testid="props.dataTestid ?? testIds.common.select.agency"
    :items="_items"
    @update:open.once="
      () => {
        fetchAgencyStructure()
      }
    "
  >
    <template v-if="$slots.trigger" #trigger>
      <slot name="trigger" />
    </template>
    <template #item="{ item }">
      <div
        class="flex grow items-center"
        :style="{
          paddingLeft: `${item.metaData.level * 8}px`
        }"
      >
        <div>{{ item.title }}</div>
      </div>
    </template>
  </ComboboxField>
</template>
